import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { forceRefresh, clearAllCaches, verifyDeployment } from '@/utils/deploymentCheck';
import { RefreshCw, Zap, CheckCircle } from 'lucide-react';

export const ManualRefreshTrigger = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const handleForceRefresh = async () => {
    setIsRefreshing(true);
    toast({
      title: "Force Refresh Initiated",
      description: "Clearing all caches and refreshing...",
    });
    
    try {
      await forceRefresh();
    } catch (error) {
      console.error('Force refresh failed:', error);
      setIsRefreshing(false);
    }
  };

  const handleVerifyDeployment = async () => {
    setIsVerifying(true);
    try {
      const result = await verifyDeployment();
      
      toast({
        title: result.success ? "Deployment Verified" : "Verification Failed",
        description: result.success 
          ? `Server timestamp: ${new Date(result.serverTime || 0).toLocaleString()}`
          : "Could not verify deployment status",
        variant: result.success ? "default" : "destructive"
      });
    } catch (error) {
      console.error('Deployment verification failed:', error);
      toast({
        title: "Verification Error",
        description: "Could not verify deployment",
        variant: "destructive"
      });
    } finally {
      setIsVerifying(false);
    }
  };

  // Only show in development or when URL param is present
  const shouldShow = import.meta.env.DEV || 
    new URLSearchParams(window.location.search).get('debug') === 'true';

  if (!shouldShow) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={handleVerifyDeployment}
        disabled={isVerifying}
        className="bg-background/80 backdrop-blur-sm"
      >
        <CheckCircle className="h-4 w-4 mr-2" />
        {isVerifying ? 'Verifying...' : 'Verify Deployment'}
      </Button>
      
      <Button
        variant="outline"
        size="sm"
        onClick={handleForceRefresh}
        disabled={isRefreshing}
        className="bg-background/80 backdrop-blur-sm"
      >
        <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
        {isRefreshing ? 'Refreshing...' : 'Force Refresh'}
      </Button>
      
      <Button
        variant="outline"
        size="sm"
        onClick={() => window.location.href = window.location.href + '?force-refresh=true'}
        className="bg-background/80 backdrop-blur-sm"
      >
        <Zap className="h-4 w-4 mr-2" />
        URL Refresh
      </Button>
    </div>
  );
};